import { ChartCanvas, Chart } from "@react-financial-charts/core";
import { XAxis, YAxis } from "@react-financial-charts/axes";
import { BarSeries, LineSeries } from "@react-financial-charts/series";
import { CrossHairCursor, MouseCoordinateX, MouseCoordinateY } from "@react-financial-charts/coordinates";
import { format } from "d3-format";
import type { FlowsPointDto } from "../api";
import {
  CHART_HEIGHT, NET_COLOR, VIX_UP_COLOR, VIX_DOWN_COLOR,
  useContainerWidth, buildScale, symmetricExtent, NotEnoughPoints,
} from "./flowsChartShared";

/** TAB 5 "FII vs DII" -- the net-net tug-of-war: (FII net - DII net) as ONE
 * 0-centered series (₹Cr). Positive = FII buying MORE than DII is (FII
 * dominant that day); negative = DII buying more than FII (or absorbing
 * FII selling) -- the single number that answers "who's actually moving
 * the market today, on net." VIX day-change bars stay as context on the
 * right axis, same convention as every other tab. The corr(VIX-change,
 * FII-DII) readout is rendered by the caller (MarketPage.tsx) alongside
 * this chart, reusing the same FlowsCorrelationOut payload Tab 1 uses.
 */

interface ChartDatum {
  date: Date;
  fii_minus_dii: number | null;
  vix_change: number | null;
}

export function FlowsNetChart({ points }: { points: FlowsPointDto[] }) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  const data: ChartDatum[] = points.map((p) => ({
    date: new Date(p.date), fii_minus_dii: p.fii_minus_dii, vix_change: p.vix_change,
  }));

  if (data.length < 2 || width === 0) {
    return data.length < 2 ? <NotEnoughPoints containerRef={ref} /> : <div ref={ref} style={{ minHeight: 40 }} />;
  }

  const { plotData, xScale, xAccessor, displayXAccessor, xExtents, formatTickDate } = buildScale(data);
  const netExtents = symmetricExtent(data.map((d) => d.fii_minus_dii), 0.1, 1);
  const vixChangeValues = data.map((d) => d.vix_change);
  const hasVix = vixChangeValues.some((v) => v != null);
  const vixExtents = symmetricExtent(vixChangeValues);

  return (
    <div ref={ref}>
      <div className="flows-legend">
        <span><i style={{ background: NET_COLOR }} /> FII net − DII net (₹cr)</span>
        {hasVix ? (
          <span>
            <i style={{ background: VIX_UP_COLOR }} />/<i style={{ background: VIX_DOWN_COLOR }} /> VIX day-change
            (right axis, context)
          </span>
        ) : null}
      </div>
      <ChartCanvas
        height={CHART_HEIGHT}
        width={width}
        ratio={window.devicePixelRatio || 1}
        margin={{ left: 56, right: hasVix ? 48 : 8, top: 8, bottom: 24 }}
        data={plotData}
        seriesName="flows-net"
        xScale={xScale}
        xAccessor={xAccessor}
        displayXAccessor={displayXAccessor}
        xExtents={xExtents}
      >
        <Chart id={0} height={CHART_HEIGHT} yExtents={netExtents} padding={{ top: 10, bottom: 10 }}>
          <XAxis showGridLines tickFormat={formatTickDate("%d %b")} />
          <YAxis showGridLines tickFormat={format(".2s")} />
          <LineSeries yAccessor={(d: ChartDatum) => d.fii_minus_dii ?? undefined} strokeStyle={NET_COLOR} strokeWidth={1.6} />
          <MouseCoordinateY displayFormat={format(".2f")} />
          <MouseCoordinateX displayFormat={formatTickDate("%d %b %Y")} />
        </Chart>
        {hasVix ? (
          <Chart id={1} height={CHART_HEIGHT} yExtents={vixExtents} padding={{ top: 10, bottom: 10 }}>
            <YAxis axisAt="right" orient="right" showGridLines={false} tickFormat={format("+.1f")} />
            <BarSeries
              yAccessor={(d: ChartDatum) => d.vix_change ?? undefined}
              baseAt={(_xScale, yScale) => yScale(0)}
              fillStyle={(d: ChartDatum) => (d.vix_change != null && d.vix_change >= 0 ? VIX_UP_COLOR : VIX_DOWN_COLOR)}
              widthRatio={0.4}
            />
          </Chart>
        ) : null}
        <CrossHairCursor />
      </ChartCanvas>
    </div>
  );
}
